import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle, ArrowRight, Star } from 'lucide-react';

const ServiceLandingPage = () => {
  const { serviceSlug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  const services = {
    'trophies': {
      title: 'Trophy Shop San Diego',
      subtitle: 'Custom Trophies Near Me • Sports & Corporate Awards',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp',
      description: 'San Diego\'s #1 trophy shop since 1995. Custom trophies for sports teams, corporate awards, schools, and special events. Best prices, fast turnaround, same-day rush available.',
      keywords: ['trophy shop san diego', 'trophies san diego', 'trophy store san diego', 'trophy shop near me', 'trophies near me', 'custom trophy near me', 'trophy stores near me', 'trophy making near me', 'san diego trophy shop', 'san diego trophy', 'trophy san diego'],
      features: [
        'Huge selection in stock',
        'Custom trophy design',
        'Same-day rush available',
        '4-day standard turnaround',
        'Best prices guaranteed',
        'Free engraving setup'
      ],
      items: [
        { name: 'Participation Trophies', price: 'From $5' },
        { name: 'Achievement Trophies', price: 'From $15' },
        { name: 'Championship Trophies', price: 'From $35' },
        { name: 'Perpetual Trophies', price: 'From $75' },
        { name: 'Crystal Trophies', price: 'From $45' },
        { name: 'Custom Design', price: 'From $50' }
      ],
      content: `
        <h2>San Diego's Best Trophy Shop</h2>
        <p>Looking for a <strong>trophy shop in San Diego</strong>? RC Awards has been the trusted choice for <strong>trophies in San Diego</strong> since 1995. Whether you need sports trophies, corporate awards, academic recognition, or custom designs, we have thousands of options in stock.</p>
        
        <h3>Trophy Shop Near Me</h3>
        <p>Located at 2509 El Cajon Blvd, our <strong>trophy store</strong> serves all of San Diego County. We're just minutes from North Park, Hillcrest, Mission Valley, La Mesa, and surrounding areas. Stop by to see our full selection!</p>
        
        <h3>Types of Trophies We Offer</h3>
        <ul>
          <li><strong>Sports Trophies</strong> - Baseball, soccer, basketball, football, swimming, martial arts, golf</li>
          <li><strong>Corporate Trophies</strong> - Employee recognition, sales awards, years of service</li>
          <li><strong>Academic Trophies</strong> - Honor roll, graduation, spelling bee, science fair</li>
          <li><strong>Championship Trophies</strong> - Large display trophies for tournaments and leagues</li>
          <li><strong>Participation Trophies</strong> - Affordable awards for entire teams</li>
          <li><strong>Crystal & Glass Trophies</strong> - Premium executive awards</li>
        </ul>
        
        <h3>Custom Trophies Near Me</h3>
        <p>Need a <strong>custom trophy</strong>? Our design team can create unique awards for any occasion. From custom figurines to specialty bases, we bring your vision to life.</p>
        
        <h3>San Diego Trophy Pricing</h3>
        <p><strong>Trophies near me</strong> pricing varies by size and style:</p>
        <ul>
          <li>Small participation (6-8"): $5-15</li>
          <li>Medium achievement (10-14"): $15-35</li>
          <li>Large championship (16"+): $35-100+</li>
          <li>Bulk discounts for 25+ trophies</li>
        </ul>
        
        <h3>Why Choose Our Trophy Store?</h3>
        <p>RC Awards is San Diego's highest-rated <strong>trophy shop</strong> with 5-star reviews. We offer the best prices, fastest turnaround, and friendliest service. Visit us today!</p>
      `
    },
    'plaques': {
      title: 'Custom Plaques San Diego',
      subtitle: 'Plaque Shop Near Me • Wood, Acrylic & Metal Plaques',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      description: 'Professional custom plaques in San Diego. Wood plaques, acrylic plaques, metal plaques, and perpetual plaques. Perfect for awards, recognition, memorials, and dedications.',
      keywords: ['plaques san diego', 'plaque shop near me', 'where to get plaques made near me', 'custom plaques', 'award plaques', 'recognition plaques', 'plaque'],
      features: [
        'Wood, acrylic & metal options',
        'Full-color printing available',
        'Custom sizes & shapes',
        'Fast turnaround',
        'Bulk discounts',
        'Free design assistance'
      ],
      items: [
        { name: 'Wood Plaques', price: 'From $25' },
        { name: 'Acrylic Plaques', price: 'From $30' },
        { name: 'Metal Plaques', price: 'From $35' },
        { name: 'Perpetual Plaques', price: 'From $75' },
        { name: 'Photo Plaques', price: 'From $40' },
        { name: 'Custom Shapes', price: 'From $45' }
      ],
      content: `
        <h2>Custom Plaques in San Diego</h2>
        <p>Searching for a <strong>plaque shop near me</strong>? RC Awards creates beautiful custom plaques for awards, recognition, memorials, dedications, and special occasions. Located in San Diego, we serve businesses, schools, and organizations throughout the county.</p>
        
        <h3>Where to Get Plaques Made Near Me</h3>
        <p>Our <strong>plaque shop</strong> at 2509 El Cajon Blvd offers a huge selection of plaque styles and materials. Visit our showroom to see samples and discuss your project with our experts.</p>
        
        <h3>Types of Plaques We Create</h3>
        <ul>
          <li><strong>Wood Plaques</strong> - Classic walnut, cherry, oak, and mahogany finishes</li>
          <li><strong>Acrylic Plaques</strong> - Modern, colorful options with clean lines</li>
          <li><strong>Metal Plaques</strong> - Brass, aluminum, and stainless steel for durability</li>
          <li><strong>Perpetual Plaques</strong> - Add names year after year</li>
          <li><strong>Photo Plaques</strong> - Full-color printed images and logos</li>
          <li><strong>Memorial Plaques</strong> - Honor loved ones and special tributes</li>
        </ul>
        
        <h3>Plaque Uses</h3>
        <ul>
          <li><strong>Employee Recognition</strong> - Years of service, employee of the month</li>
          <li><strong>Academic Awards</strong> - Honor roll, graduation, achievement</li>
          <li><strong>Sports Awards</strong> - MVP, championship, team recognition</li>
          <li><strong>Memorial & Dedication</strong> - Benches, buildings, memorials</li>
          <li><strong>Business Awards</strong> - Client appreciation, partnerships</li>
        </ul>
        
        <h3>Custom Plaque Design</h3>
        <p>Need something unique? Our design team can create custom plaques with your logo, photos, and personalized text. We offer free design consultations and proofs before production.</p>
        
        <h3>Fast Turnaround</h3>
        <p>Standard plaque orders completed in 4-5 business days. Rush service available for urgent needs. Contact us at (619) 640-1633 for rush orders.</p>
      `
    },
    'engraving': {
      title: 'Custom Engraving San Diego',
      subtitle: 'Same Day Engraving Services Available',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      description: 'Professional laser and rotary engraving services in San Diego. From trophies and plaques to jewelry and gifts, we engrave it all with precision and care.',
      keywords: ['engraving san diego', 'same day engraving', 'laser engraving', 'engraving near me', 'custom engraving'],
      features: [
        'Same-day engraving available',
        'Laser & rotary engraving',
        'Works on metal, wood, glass, acrylic',
        'Photo & logo engraving',
        'Walk-ins welcome',
        'Free quotes'
      ],
      items: [
        { name: 'Trophies & Awards', price: 'From $5' },
        { name: 'Plaques', price: 'From $8' },
        { name: 'Name Badges', price: 'From $6' },
        { name: 'Pet Tags', price: 'From $5' },
        { name: 'Jewelry', price: 'From $10' },
        { name: 'Gifts & Tools', price: 'From $8' }
      ],
      content: `
        <h2>Professional Engraving Services in San Diego</h2>
        <p>Looking for <strong>engraving services in San Diego</strong>? RC Awards offers professional laser and rotary engraving for trophies, plaques, name badges, jewelry, gifts, and more. With nearly 30 years of experience, we deliver precision engraving with fast turnaround times.</p>
        
        <h3>Same Day Engraving San Diego</h3>
        <p>Need <strong>same day engraving</strong>? We offer rush services for last-minute needs. Most items can be engraved while you wait in 30-60 minutes. Call ahead at (619) 640-1633 to confirm availability.</p>
        
        <h3>What We Engrave</h3>
        <ul>
          <li><strong>Trophies & Awards</strong> - Add names, dates, and achievements</li>
          <li><strong>Plaques</strong> - Wood, acrylic, and metal plaques</li>
          <li><strong>Name Badges</strong> - Professional badges for businesses</li>
          <li><strong>Jewelry</strong> - Bracelets, pendants, rings, watches</li>
          <li><strong>Gifts</strong> - Picture frames, flasks, pens, tools</li>
          <li><strong>Pet Tags</strong> - ID tags for dogs and cats</li>
          <li><strong>Electronics</strong> - iPads, laptops, phones</li>
        </ul>
        
        <h3>Engraving Methods</h3>
        <p><strong>Laser Engraving:</strong> High-precision engraving that works on metal, wood, glass, acrylic, and leather. Perfect for detailed logos and photos.</p>
        <p><strong>Rotary Engraving:</strong> Traditional deep-cut engraving ideal for brass plates, name badges, and industrial tags.</p>
        
        <h3>Serving All of San Diego County</h3>
        <p>Our engraving shop on El Cajon Blvd is conveniently located to serve North Park, Hillcrest, Mission Valley, La Mesa, El Cajon, Chula Vista, La Jolla, and all surrounding areas.</p>
      `
    },
    'embroidery': {
      title: 'Custom Embroidery San Diego',
      subtitle: 'Professional Logo Embroidery for Businesses & Teams',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/ehppwmya_unnamed%20%287%29.webp',
      description: 'High-quality custom embroidery services in San Diego. Perfect for company uniforms, team apparel, promotional items, and personalized gifts.',
      keywords: ['embroidery san diego', 'custom embroidery', 'logo embroidery', 'embroidered shirts', 'embroidery near me'],
      features: [
        'Company logo embroidery',
        'Team uniforms & apparel',
        'No minimums on many items',
        'Free digitizing with orders',
        'Fast turnaround',
        'Quality thread & materials'
      ],
      items: [
        { name: 'Polo Shirts', price: 'From $18' },
        { name: 'Jackets', price: 'From $35' },
        { name: 'Hats & Caps', price: 'From $12' },
        { name: 'Bags & Totes', price: 'From $15' },
        { name: 'Uniforms', price: 'From $20' },
        { name: 'Towels', price: 'From $10' }
      ],
      content: `
        <h2>Custom Embroidery Services in San Diego</h2>
        <p>Looking for <strong>custom embroidery in San Diego</strong>? RC Awards provides professional embroidery services for businesses, sports teams, schools, and organizations. We embroider company logos, names, and custom designs on polos, jackets, hats, bags, and more.</p>
        
        <h3>Business & Corporate Embroidery</h3>
        <p>Give your team a professional look with <strong>embroidered company apparel</strong>. We serve San Diego businesses including restaurants, real estate companies, construction firms, healthcare providers, and more.</p>
        
        <h3>What We Embroider</h3>
        <ul>
          <li><strong>Polo Shirts</strong> - Classic business casual with your logo</li>
          <li><strong>Jackets & Outerwear</strong> - Keep your team warm and branded</li>
          <li><strong>Hats & Caps</strong> - Baseball caps, beanies, visors</li>
          <li><strong>Bags & Totes</strong> - Duffel bags, backpacks, tote bags</li>
          <li><strong>Chef Coats & Aprons</strong> - For restaurants and kitchens</li>
          <li><strong>Medical Scrubs</strong> - For healthcare professionals</li>
        </ul>
        
        <h3>Our Embroidery Process</h3>
        <p>We use state-of-the-art embroidery machines and premium threads to ensure your logos look crisp and professional. Our team provides free digitizing services to convert your logo into embroidery format.</p>
        
        <h3>Fast Turnaround & Competitive Pricing</h3>
        <p>Most embroidery orders are completed within 5-7 business days. Rush service available. Bulk discounts for large orders.</p>
      `
    },
    'screen-printing': {
      title: 'Screen Printing San Diego',
      subtitle: 'Custom T-Shirts & Apparel Printing',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/oumok1jw_unnamed%20%286%29.webp',
      description: 'Professional screen printing services in San Diego. Perfect for custom t-shirts, team uniforms, event merchandise, and promotional apparel.',
      keywords: ['screen printing san diego', 'custom t-shirts', 't-shirt printing', 'screen printing near me', 'bulk t-shirts'],
      features: [
        'Multi-color printing',
        'Bulk order discounts',
        'Fast turnaround',
        'Quality inks & materials',
        'Design assistance available',
        'Wide garment selection'
      ],
      items: [
        { name: 'T-Shirts', price: 'From $8' },
        { name: 'Hoodies', price: 'From $22' },
        { name: 'Tank Tops', price: 'From $10' },
        { name: 'Long Sleeves', price: 'From $12' },
        { name: 'Youth Shirts', price: 'From $7' },
        { name: 'Tote Bags', price: 'From $5' }
      ],
      content: `
        <h2>Custom Screen Printing in San Diego</h2>
        <p>Need <strong>custom screen printed t-shirts in San Diego</strong>? RC Awards offers professional screen printing services for businesses, teams, events, schools, and organizations. From single-color prints to complex multi-color designs, we deliver quality results.</p>
        
        <h3>Perfect For</h3>
        <ul>
          <li><strong>Sports Teams</strong> - Uniforms, fan gear, tournament shirts</li>
          <li><strong>Companies</strong> - Employee shirts, promotional giveaways</li>
          <li><strong>Events</strong> - Fundraisers, 5K runs, festivals, reunions</li>
          <li><strong>Schools</strong> - Spirit wear, club shirts, graduation</li>
          <li><strong>Bands & Artists</strong> - Merchandise and tour shirts</li>
        </ul>
        
        <h3>Screen Printing vs. Embroidery</h3>
        <p><strong>Screen Printing</strong> is best for: Large orders, detailed multi-color designs, t-shirts, and lower cost per unit.</p>
        <p><strong>Embroidery</strong> is best for: Polos, jackets, hats, and a premium professional look.</p>
        
        <h3>Our Screen Printing Process</h3>
        <p>We use high-quality plastisol inks that are durable and vibrant. Our experienced team ensures proper alignment and consistent coverage across your entire order.</p>
        
        <h3>Bulk Pricing</h3>
        <p>The more you order, the more you save! Contact us for bulk pricing on orders of 24, 48, 72, or 100+ pieces.</p>
      `
    },
    'design': {
      title: 'Award Design Services San Diego',
      subtitle: 'Custom Trophy & Award Creation',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp',
      description: 'Professional award design services in San Diego. We help you create custom trophies, plaques, and recognition awards that make an impact.',
      keywords: ['custom awards', 'trophy design', 'award creation', 'custom trophies', 'recognition awards'],
      features: [
        'Free design consultations',
        'Custom trophy creation',
        'Logo design assistance',
        'Proof approval process',
        'Unique one-of-a-kind pieces',
        'Budget-friendly options'
      ],
      items: [
        { name: 'Design Consultation', price: 'Free' },
        { name: 'Logo Vectorization', price: 'From $25' },
        { name: 'Custom Trophy Design', price: 'From $50' },
        { name: 'Proof & Revisions', price: 'Included' },
        { name: 'Rush Design', price: 'From $35' },
        { name: 'Full Branding Package', price: 'From $150' }
      ],
      content: `
        <h2>Custom Award Design Services</h2>
        <p>Want to create a <strong>custom award or trophy</strong> that stands out? Our design team helps you conceptualize and create unique recognition pieces for any occasion. From corporate awards to sports championships, we bring your vision to life.</p>
        
        <h3>Our Design Services Include</h3>
        <ul>
          <li><strong>Free Consultations</strong> - Discuss your vision and budget</li>
          <li><strong>Logo Preparation</strong> - Convert your logo for engraving or printing</li>
          <li><strong>Custom Layouts</strong> - Professional text and design arrangement</li>
          <li><strong>Proof Approval</strong> - Review before production</li>
          <li><strong>Revisions</strong> - We work until you're happy</li>
        </ul>
        
        <h3>Custom Award Ideas</h3>
        <ul>
          <li>Perpetual trophies with annual name plates</li>
          <li>Crystal awards with 3D laser etching</li>
          <li>Custom-shaped acrylic awards</li>
          <li>Photo plaques with full-color printing</li>
          <li>Combination trophy and plaque displays</li>
        </ul>
        
        <h3>How It Works</h3>
        <ol>
          <li><strong>Consultation</strong> - Tell us about your award needs</li>
          <li><strong>Design</strong> - We create a custom layout</li>
          <li><strong>Proof</strong> - Review and approve the design</li>
          <li><strong>Production</strong> - We create your award</li>
          <li><strong>Delivery</strong> - Pick up or we deliver</li>
        </ol>
      `
    },
    'promotional-products': {
      title: 'Promotional Products San Diego',
      subtitle: 'Custom Branded Merchandise & Giveaways',
      heroImage: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      description: 'Custom promotional products and branded merchandise in San Diego. Pens, drinkware, bags, tech accessories, and more with your logo.',
      keywords: ['promotional products san diego', 'branded merchandise', 'custom promotional items', 'swag', 'corporate gifts'],
      features: [
        'Thousands of products',
        'Full-color printing available',
        'Low minimums',
        'Fast turnaround',
        'Free samples available',
        'Brand consulting'
      ],
      items: [
        { name: 'Pens', price: 'From $0.50' },
        { name: 'Drinkware', price: 'From $3' },
        { name: 'Tote Bags', price: 'From $2' },
        { name: 'Tech Accessories', price: 'From $5' },
        { name: 'Notebooks', price: 'From $4' },
        { name: 'Stress Balls', price: 'From $1' }
      ],
      content: `
        <h2>Promotional Products in San Diego</h2>
        <p>Looking for <strong>promotional products in San Diego</strong>? RC Awards offers thousands of brandable items perfect for trade shows, corporate events, employee gifts, and marketing campaigns. Get your logo on pens, drinkware, bags, tech accessories, and more.</p>
        
        <h3>Popular Promotional Products</h3>
        <ul>
          <li><strong>Writing Instruments</strong> - Pens, pencils, highlighters</li>
          <li><strong>Drinkware</strong> - Water bottles, tumblers, mugs</li>
          <li><strong>Bags</strong> - Tote bags, backpacks, drawstrings</li>
          <li><strong>Tech Items</strong> - Power banks, phone stands, USB drives</li>
          <li><strong>Apparel</strong> - T-shirts, hats, sunglasses</li>
          <li><strong>Office</strong> - Notebooks, calendars, desk items</li>
          <li><strong>Eco-Friendly</strong> - Recycled materials, bamboo items</li>
        </ul>
        
        <h3>Perfect For</h3>
        <ul>
          <li>Trade shows and conventions</li>
          <li>Corporate events and meetings</li>
          <li>Employee appreciation gifts</li>
          <li>Customer thank-you gifts</li>
          <li>Grand openings and launches</li>
          <li>Fundraisers and charity events</li>
        </ul>
        
        <h3>Why Promotional Products Work</h3>
        <p>Studies show promotional products have a higher recall rate than other advertising methods. Your customers keep and use branded items, keeping your business top of mind.</p>
      `
    }
  };

  const service = services[serviceSlug];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#1a365d] text-white pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d] via-[#1a365d]/90 to-[#1a365d]/70" />
        
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-[#D4AF37] text-[#D4AF37]" />
              ))}
              <span className="text-white/80 text-sm ml-2">5.0 Rating • 200+ Reviews</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-[#D4AF37] mb-6">
              {service.subtitle}
            </p>
            <p className="text-lg text-white/80 max-w-2xl mb-8">
              {service.description}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:6196401633"
                className="bg-[#D4AF37] text-[#1a365d] px-8 py-4 rounded-full font-semibold hover:bg-[#c9a432] transition-colors flex items-center gap-2"
              >
                <Phone size={20} />
                Call (619) 640-1633
              </a>
              <Link
                to="/contact"
                className="bg-white text-[#1a365d] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                Get Free Quote
                <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features & Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Why Choose Us</h2>
              <div className="space-y-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-[#D4AF37] flex-shrink-0" size={20} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Pricing */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Pricing</h2>
              <div className="space-y-3">
                {service.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                    <span className="text-gray-700">{item.name}</span>
                    <span className="font-semibold text-[#1a365d]">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4">
                * Prices vary based on quantity and complexity. Contact us for a custom quote.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: service.content }}
          />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#1a365d]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
              <p className="text-white/80 mb-8">
                Contact us today for a free quote. Visit our showroom to see samples in person, or call us to discuss your project.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                    <Phone className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Call or Text</p>
                    <a href="tel:6196401633" className="text-lg font-semibold hover:text-[#D4AF37]">
                      (619) 640-1633
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                    <Mail className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Email Us</p>
                    <a href="mailto:RCAwards@Aol.com" className="text-lg font-semibold hover:text-[#D4AF37]">
                      RCAwards@Aol.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                    <MapPin className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Visit Our Shop</p>
                    <p className="text-lg font-semibold">2509 El Cajon Blvd #A, San Diego</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                    <Clock className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Business Hours</p>
                    <p className="text-lg font-semibold">Mon-Fri 9-6, Sat 10-1</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold text-[#1a365d] mb-6">Request a Free Quote</h3>
              <form className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Tell us about your project..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#D4AF37] text-[#1a365d] py-3 rounded-lg font-semibold hover:bg-[#c9a432] transition-colors"
                >
                  Get Free Quote
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#1a365d] mb-8 text-center">Our Other Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(services).filter(([slug]) => slug !== serviceSlug).map(([slug, svc]) => (
              <Link
                key={slug}
                to={`/services/${slug}`}
                className="bg-white p-4 rounded-xl text-center hover:shadow-md transition-shadow"
              >
                <p className="font-medium text-[#1a365d] text-sm">{svc.title.replace(' San Diego', '').replace(' Services', '')}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceLandingPage;
